import React, { useEffect, useState } from "react";
import {
  CheckCircle,
  XCircle,
  Trophy,
  User,
  BookOpen,
  Target,
  AlertCircle,
  TrendingUp,
  Award,
  Eye,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  AlertTriangle,
  Play,
  Lightbulb,
} from "lucide-react";
import Modal from "../../../../components/ui/Modal";
import { ApiService } from "../../../../services/api";

interface HomeworkReport {
  id: string;
  reportData: any;
  reportHtml: string;
  metadata?: any;
  createdAt: string;
  updatedAt: string;
  homework: {
    id: string;
    title: string;
    status: string;
    createdAt: string;
    deadline: string;
    finishedAt?: string;
    lesson: {
      id: string;
      title: string;
    };
    lessonSection: {
      id: string;
      title: string;
    };
    lessonSectionContent: {
      id: string;
      title: string;
      type: string;
    };
  };
  user: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    metadata?: {
      schoolNumber?: string;
      tcNumber?: string;
      parentPhone?: string;
      parentEmail?: string;
      [key: string]: any;
    };
    department?: {
      id: string;
      name: string;
      color: string;
    };
  };
}

interface WeakTopic {
  gainId: string;
  gainText: string;
  startTime?: string;
  endTime?: string;
  questionCount: number;
  questionOrders: number[];
  topic?: { id: string; title: string } | null;
  lesson?: { id: string; title: string } | null;
  content?: { id: string; title: string; type: string } | null;
  video?: { id: string; title: string; type: string; url: string | null } | null;
}

interface StudentReportViewProps {
  homeworkReport: HomeworkReport;
  onClose?: () => void;
  hideDeadline?: boolean;
}

export default function StudentReportView({ homeworkReport, onClose, hideDeadline }: StudentReportViewProps) {
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [showStudentInfo, setShowStudentInfo] = useState(true);
  const [showParentInfo, setShowParentInfo] = useState(false);
  const [showLessonInfo, setShowLessonInfo] = useState(false);
  const [showDateInfo, setShowDateInfo] = useState(false);
  const [showWeakTopics, setShowWeakTopics] = useState(true);

  const summarySafe = (homeworkReport as any)?.reportData?.summary || {};
  const scorePercent: number = Number(summarySafe?.score_percent ?? 0);
  const totalQuestions: number = Number(summarySafe?.total_questions ?? 0);
  const correctCount: number = Number(summarySafe?.correct ?? 0);
  const wrongCount: number = Number(summarySafe?.wrong ?? 0);
  const blankCount: number = Number(
    summarySafe?.blank ??
    summarySafe?.empty ??
    summarySafe?.unanswered ??
    (totalQuestions > 0 ? Math.max(0, totalQuestions - correctCount - wrongCount) : 0)
  );
  const [questionOrderMap, setQuestionOrderMap] = useState<Record<string, number>>({});

  const [dynamicWeakTopics, setDynamicWeakTopics] = useState<WeakTopic[]>([]);
  const [dynamicWrongTopics, setDynamicWrongTopics] = useState<WeakTopic[]>([]);
  const [dynamicUnansweredTopics, setDynamicUnansweredTopics] = useState<WeakTopic[]>([]);
  const [isLoadingWeakTopics, setIsLoadingWeakTopics] = useState(false);

  const reportWeakTopics = (homeworkReport as any)?.reportData?.weakTopics;
  const reportWrongTopics = (homeworkReport as any)?.reportData?.wrongTopics;
  const reportUnansweredTopics = (homeworkReport as any)?.reportData?.unansweredTopics;

  const weakTopics: WeakTopic[] = reportWeakTopics || dynamicWeakTopics;
  const wrongTopics: WeakTopic[] = reportWrongTopics || dynamicWrongTopics;
  const unansweredTopics: WeakTopic[] = reportUnansweredTopics || dynamicUnansweredTopics;

  const hasWeakTopics = weakTopics.length > 0 || wrongTopics.length > 0 || unansweredTopics.length > 0;

  const groupedWeakTopics = React.useMemo(() => {
    const groups: { key: string; topics: WeakTopic[] }[] = [];
    weakTopics.forEach((topic) => {
      const key = [...(topic.questionOrders || [])].sort((a, b) => a - b).join(",");
      const existingGroup = groups.find((g) => g.key === key);
      if (existingGroup) {
        existingGroup.topics.push(topic);
      } else {
        groups.push({ key, topics: [topic] });
      }
    });
    return groups.sort((a, b) => {
      const orderA = a.topics[0]?.questionOrders?.[0] ?? Infinity;
      const orderB = b.topics[0]?.questionOrders?.[0] ?? Infinity;

      return orderA - orderB;
    });
  }, [weakTopics]);

  useEffect(() => {
    if (!reportWeakTopics && homeworkReport.id && !isLoadingWeakTopics) {
      const fetchWeakTopics = async () => {
        setIsLoadingWeakTopics(true);
        try {
          const data = await ApiService.getWeakTopicsForHomeworkReport(homeworkReport.id);
          if (data) {
            setDynamicWeakTopics(data.weakTopics || []);
            setDynamicWrongTopics(data.wrongTopics || []);
            setDynamicUnansweredTopics(data.unansweredTopics || []);
          }
        } catch (error) {
          console.error("Failed to fetch weak topics:", error);
        } finally {
          setIsLoadingWeakTopics(false);
        }
      };

      fetchWeakTopics();
    }
  }, [homeworkReport.id, reportWeakTopics]);

  useEffect(() => {
    const loadOrder = async () => {
      try {
        const lessonId = (homeworkReport as any)?.homework?.lesson?.id;
        const sectionId = (homeworkReport as any)?.homework?.lessonSection?.id;
        const contentId = (homeworkReport as any)?.homework?.lessonSectionContent?.id;
        const items: any[] = (((homeworkReport as any)?.reportData?.items as any[]) ?? []) as any[];
        if (Array.isArray(items) && items.some((it) => typeof (it as any)?.order === "number")) {
          setQuestionOrderMap({});
          return;
        }
        if (!lessonId || !sectionId || !contentId) return;
        const orderMap = await ApiService.getExamQuestionOrderMap(lessonId, sectionId, contentId);
        setQuestionOrderMap(orderMap);
      } catch (_) {
        setQuestionOrderMap({});
      }
    };
    loadOrder();
  }, [homeworkReport]);

  const questionsItems: any[] = (() => {
    const rawItems = (((homeworkReport as any)?.reportData?.items as any[]) ?? []) as any[];
    const prepared = rawItems.map((item, index) => {
      const qid = String((item as any)?.qid || "");
      const mappedOrder = questionOrderMap[qid];
      const sortKey = Number.isFinite(mappedOrder) ? mappedOrder : index + 1;
      return { ...item, _qid: qid, _sortKey: sortKey };
    });

    const sorted = prepared.sort((a: any, b: any) => Number(a?._sortKey || 0) - Number(b?._sortKey || 0));

    return sorted.map((item: any, idx: number) => ({
      ...item,
      displayIndex: idx + 1,
    }));
  })();

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPhoneNumber = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, "");
    if (cleanPhone.length === 10 && !cleanPhone.startsWith("0")) {
      return "0" + cleanPhone;
    }
    return cleanPhone.startsWith("0") ? cleanPhone : "0" + cleanPhone;
  };

  const getContentTypeText = (type: string) => {
    switch (type) {
      case "VIDEO":
        return "Video";
      case "AUDIO":
        return "Ses";
      case "IMAGE":
        return "Resim";
      case "TEXT":
        return "Metin";
      case "FILE":
        return "Dosya";
      case "PDF":
        return "PDF";
      case "CODE":
        return "Kod";
      case "TEST":
      case "EXAM":
        return "Test";
      default:
        return "İçerik";
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return "text-green-600";
    if (score >= 60) return "text-blue-600";
    if (score >= 40) return "text-yellow-600";
    return "text-red-600";
  };

  const getPerformanceLevel = (score: number) => {
    if (score >= 90) return { text: "Mükemmel", icon: Trophy, color: "text-yellow-600" };
    if (score >= 80) return { text: "Çok İyi", icon: Award, color: "text-green-600" };
    if (score >= 70) return { text: "İyi", icon: TrendingUp, color: "text-blue-600" };
    if (score >= 50) return { text: "Orta", icon: Target, color: "text-orange-600" };
    return { text: "Geliştirilmeli", icon: AlertCircle, color: "text-red-600" };
  };

  const performance = getPerformanceLevel(scorePercent);
  const PerformanceIcon = performance.icon;

  return (
    <div className="w-full">

      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-50 to-blue-50 border border-gray-200 shadow-lg mb-6">

        <div className="pointer-events-none absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-100/50 to-purple-100/50 rounded-full -translate-y-16 translate-x-16 -z-10"></div>
        <div className="pointer-events-none absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-green-100/30 to-blue-100/30 rounded-full translate-y-12 -translate-x-12 -z-10"></div>

        <div className="relative p-6">

          <div className="text-center mb-6">
            <div className="flex justify-center mb-3">
              <div className="relative w-32 h-32">

                <svg className="w-32 h-32 transform -rotate-90" viewBox="0 0 120 120">
                  <circle cx="60" cy="60" r="54" stroke="currentColor" strokeWidth="8" fill="transparent" className="text-gray-200" />

                  <circle
                    cx="60"
                    cy="60"
                    r="54"
                    stroke="currentColor"
                    strokeWidth="8"
                    fill="transparent"
                    strokeDasharray={`${2 * Math.PI * 54}`}
                    strokeDashoffset={`${2 * Math.PI * 54 * (1 - scorePercent / 100)}`}
                    className={`transition-all duration-1000 ease-out ${scorePercent >= 80
                      ? "text-green-500"
                      : scorePercent >= 60
                        ? "text-blue-500"
                        : scorePercent >= 40
                          ? "text-yellow-500"
                          : "text-red-500"
                      }`}
                    strokeLinecap="round"
                  />
                </svg>

                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center">
                    <div className="text-sm font-medium text-gray-600 mb-1">Başarı Oranı</div>
                    <div className={`text-2xl font-black ${getScoreColor(scorePercent)}`}>%{Math.round(scorePercent)}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/80 backdrop-blur-sm border border-gray-200 shadow-sm">
              <PerformanceIcon className={`w-5 h-5 ${performance.color}`} />
              <span className={`text-base font-bold ${performance.color}`}>{performance.text}</span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2">

            <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 border border-white/50">
              <div className="text-[10px] font-medium text-green-700 mb-1 text-center">DOĞRU</div>
              <div className="w-full bg-green-100 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-green-400 to-green-500 h-1.5 rounded-full transition-all duration-1000"
                  style={{
                    width: `${totalQuestions > 0 ? (correctCount / totalQuestions) * 100 : 0}%`,
                  }}></div>
              </div>
              <div className="text-xs font-bold text-green-700 text-center mt-1">
                {correctCount}/{totalQuestions}
              </div>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 border border-white/50">
              <div className="text-[10px] font-medium text-red-700 mb-1 text-center">YANLIŞ</div>
              <div className="w-full bg-red-100 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-red-400 to-red-500 h-1.5 rounded-full transition-all duration-1000"
                  style={{
                    width: `${totalQuestions > 0 ? (wrongCount / totalQuestions) * 100 : 0}%`,
                  }}></div>
              </div>
              <div className="text-xs font-bold text-red-700 text-center mt-1">
                {wrongCount}/{totalQuestions}
              </div>
            </div>

            <div className="bg-white/60 backdrop-blur-sm rounded-lg p-2 border border-white/50">
              <div className="text-[10px] font-medium text-amber-700 mb-1 text-center">BOŞ</div>
              <div className="w-full bg-amber-100 rounded-full h-1.5">
                <div
                  className="bg-gradient-to-r from-amber-400 to-amber-500 h-1.5 rounded-full transition-all duration-1000"
                  style={{
                    width: `${totalQuestions > 0 ? (blankCount / totalQuestions) * 100 : 0}%`,
                  }}></div>
              </div>
              <div className="text-xs font-bold text-amber-700 text-center mt-1">
                {blankCount}/{totalQuestions}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3 mb-4">

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            onClick={() => setShowStudentInfo(!showStudentInfo)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Öğrenci Bilgileri</h3>
            </div>
            {showStudentInfo ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          </button>

          {showStudentInfo && (
            <div className="px-4 pb-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">Ad Soyad:</span> {homeworkReport.user.firstName} {homeworkReport.user.lastName}
                </p>
                <p className="text-xs text-gray-600">
                  <span className="font-medium">E-posta:</span>{" "}
                  <a
                    href={`mailto:${homeworkReport.user.email}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                    {homeworkReport.user.email}
                  </a>
                </p>
                {homeworkReport.user.department && (
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Departman:</span> {homeworkReport.user.department.name}
                  </p>
                )}
                {homeworkReport.user.metadata?.schoolNumber && (
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Okul No:</span> {homeworkReport.user.metadata.schoolNumber}
                  </p>
                )}
                {homeworkReport.user.metadata?.tcNumber && (
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">TC No:</span> {homeworkReport.user.metadata.tcNumber}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            onClick={() => setShowParentInfo(!showParentInfo)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Veli Bilgileri</h3>
            </div>
            {showParentInfo ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          </button>

          {showParentInfo && (
            <div className="px-4 pb-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                {homeworkReport.user.metadata?.parentPhone || homeworkReport.user.metadata?.parentEmail ? (
                  <div className="space-y-1">
                    {homeworkReport.user.metadata?.parentPhone && (
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">Telefon:</span>{" "}
                        <a
                          href={`tel:${formatPhoneNumber(homeworkReport.user.metadata.parentPhone)}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                          {formatPhoneNumber(homeworkReport.user.metadata.parentPhone)}
                        </a>
                      </p>
                    )}
                    {homeworkReport.user.metadata?.parentEmail && (
                      <p className="text-xs text-gray-600">
                        <span className="font-medium">E-posta:</span>{" "}
                        <a
                          href={`mailto:${homeworkReport.user.metadata.parentEmail}`}
                          className="text-blue-600 hover:text-blue-800 hover:underline transition-colors">
                          {homeworkReport.user.metadata.parentEmail}
                        </a>
                      </p>
                    )}
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <span className="text-xs text-gray-500 italic">Veli bilgisi bulunamadı</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            onClick={() => setShowLessonInfo(!showLessonInfo)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Ders Bilgileri</h3>
            </div>
            {showLessonInfo ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          </button>

          {showLessonInfo && (
            <div className="px-4 pb-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Ders:</span> {homeworkReport.homework.lesson.title}
                  </p>
                  <a
                    href={`/lessons/play/${homeworkReport.homework.lesson.id}?contentId=${homeworkReport.homework.lessonSectionContent.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-white rounded-md transition-colors"
                    title="İçeriğe Git">
                    <ExternalLink className="w-3 h-3" />
                    Git
                  </a>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Bölüm:</span> {homeworkReport.homework.lessonSection.title}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">İçerik:</span> {homeworkReport.homework.lessonSectionContent.title}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="font-medium">Tür:</span> {getContentTypeText(homeworkReport.homework.lessonSectionContent.type)}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
          <button
            onClick={() => setShowDateInfo(!showDateInfo)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-gray-600" />
              <h3 className="text-sm font-semibold text-gray-900">Tarih Bilgileri</h3>
            </div>
            {showDateInfo ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
          </button>

          {showDateInfo && (
            <div className="px-4 pb-4">
              <div className="bg-gray-50 rounded-lg p-3 space-y-1">
                <p className="text-xs text-gray-600">
                  <span className="font-medium">
                    {homeworkReport.homework.status === "COMPLETED" ? "Tamamlanma" : "Oluşturulma"} Tarihi:
                  </span>{" "}
                  {formatDate(
                    homeworkReport.homework.status === "COMPLETED" && (homeworkReport.homework as any)?.finishedAt
                      ? (homeworkReport.homework as any).finishedAt
                      : homeworkReport.createdAt
                  )}
                </p>
                {!hideDeadline && (
                  <>
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Son Tarih:</span> {formatDate(homeworkReport.homework.deadline)}
                    </p>
                    {new Date(homeworkReport.homework.deadline) < new Date() && (
                      <div className="text-xs text-red-600 font-medium">SÜRE GEÇTİ</div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {(homeworkReport as any)?.reportData?.summary?.comment && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-amber-600 mt-1" />
            <div>
              <h3 className="text-sm font-semibold text-amber-800 mb-1">Deep Yorumu</h3>
              <p className="text-xs text-amber-700 leading-relaxed">{(homeworkReport as any)?.reportData?.summary?.comment}</p>
            </div>
          </div>
        </div>
      )}

      {(hasWeakTopics || isLoadingWeakTopics) && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-4">
          <button
            onClick={() => setShowWeakTopics(!showWeakTopics)}
            className="w-full flex items-center justify-between p-4 text-left hover:bg-gray-50 transition-colors">
            <div className="flex items-center gap-2">
              <Lightbulb className="w-4 h-4 text-orange-600" />
              <h3 className="text-sm font-semibold text-gray-900">
                Eksik Konular
                {isLoadingWeakTopics ? (
                  <span className="ml-2 text-xs font-normal text-gray-500 animate-pulse">
                    (Yükleniyor...)
                  </span>
                ) : (
                  <span className="ml-2 text-xs font-normal text-gray-500">
                    ({weakTopics.length} konu, {new Set(weakTopics.flatMap(t => t.questionOrders || [])).size} soru)
                  </span>
                )}
              </h3>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-orange-600 font-medium bg-orange-50 px-2 py-1 rounded-full">
                Çalışması Gereken Konular
              </span>
              {showWeakTopics ? <ChevronUp className="w-4 h-4 text-gray-500" /> : <ChevronDown className="w-4 h-4 text-gray-500" />}
            </div>
          </button>

          {showWeakTopics && (
            <div className="px-4 pb-4">
              {isLoadingWeakTopics ? (
                <div className="flex items-center justify-center py-6 text-gray-500 gap-2">
                  <div className="w-4 h-4 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-xs">Eksik konular analiz ediliyor...</span>
                </div>
              ) : (
                <>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" />
                      <p className="text-xs text-orange-700">
                        Aşağıdaki konular, yanlış yaptığı veya boş bıraktığı sorulardan tespit edilmiştir.
                        Bu konuları çalıştırarak öğrencinin performansını artırabilirsiniz.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {groupedWeakTopics.map((group, index) => {
                      const firstTopic = group.topics[0];
                      return (
                        <div
                          key={group.key || index}
                          className="bg-gray-50 rounded-lg p-3 border border-gray-200 hover:border-orange-300 transition-colors">
                          <div className="flex items-start gap-2">
                            <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-orange-500 text-white text-[10px] font-bold flex-shrink-0 mt-0.5">
                              {firstTopic.questionOrders?.join(", ") || index + 1}
                            </span>

                            <div className="flex-1 min-w-0">
                              <div className="space-y-3">
                                {group.topics.map((topic, tIdx) => (
                                  <div key={tIdx} className="flex flex-col gap-1">
                                    <div className="flex justify-between items-start gap-2">
                                      <span className="text-xs font-semibold text-gray-900 leading-tight">
                                        {topic.gainText}
                                      </span>
                                      {topic.video?.url && (
                                        <a
                                          href={topic.video.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 hover:bg-green-200 rounded-md text-[10px] font-medium transition-colors"
                                          title="Konu Anlatım Videosunu İzle">
                                          <Play className="w-3 h-3" />
                                          Video
                                        </a>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>

                              {(firstTopic.lesson?.title || firstTopic.topic?.title) && (
                                <div className="mt-3 pt-2 border-t border-gray-200/50">
                                  <div className="flex flex-wrap items-center gap-2 text-[10px] text-gray-500 mt-1">
                                    {firstTopic.lesson?.title && (
                                      <span className="bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                                        {firstTopic.lesson.title}
                                      </span>
                                    )}
                                    {firstTopic.topic?.title && (
                                      <span className="bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                        {firstTopic.topic.title}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {(wrongTopics.length > 0 || unansweredTopics.length > 0) && (
                    <div className="mt-3 flex items-center gap-4 text-[10px] text-gray-500 border-t border-gray-200 pt-3">
                      {wrongTopics.length > 0 && (
                        <div className="flex items-center gap-1">
                          <XCircle className="w-3 h-3 text-red-500" />
                          <span>Yanlış cevaplara göre: <strong className="text-red-600">{wrongTopics.length}</strong> konu</span>
                        </div>
                      )}
                      {unansweredTopics.length > 0 && (
                        <div className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3 text-amber-500" />
                          <span>Boş bırakılanlara göre: <strong className="text-amber-600">{unansweredTopics.length}</strong> konu</span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      )}

      <div className="bg-gray-50 rounded-lg p-3">
        <button
          onClick={() => setShowQuestionsModal(true)}
          className="w-full flex items-center justify-center gap-2 py-3 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors">
          <Eye className="w-4 h-4" />
          <span className="text-sm font-medium">Soru Detaylarını Görüntüle ({totalQuestions} Soru)</span>
        </button>
      </div>

      {showQuestionsModal && (
        <Modal isOpen={showQuestionsModal} onClose={() => setShowQuestionsModal(false)} title="Soru Detayları" size="lg">
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-2 mb-3">
              <div className="flex items-center justify-between text-xs">
                <span className="text-blue-700 font-medium">Toplam Soru: {totalQuestions}</span>
                <div className="flex items-center gap-3">
                  <span className="text-green-700 font-medium">✓ Doğru: {correctCount}</span>
                  <span className="text-red-700 font-medium">✗ Yanlış: {wrongCount}</span>
                  <span className="text-amber-700 font-medium">• Boş: {blankCount}</span>
                </div>
              </div>
            </div>

            <div className="space-y-2 max-h-96 overflow-y-auto">
              {questionsItems.map((item: any, index: number) => {
                const resultStr = String(item?.result ?? "").toLowerCase();
                const isCorrect = resultStr === "correct";
                const isBlank = resultStr === "unanswered";

                const studentAnswerStr = String(item?.studentAnswer ?? "");

                let correctAnswerStr = "";
                if (item?.correctAnswer !== undefined && item?.correctAnswer !== null) {
                  if (typeof item.correctAnswer === "object") {
                    correctAnswerStr = item.correctAnswer.text || item.correctAnswer.value || JSON.stringify(item.correctAnswer);
                  } else {
                    correctAnswerStr = String(item.correctAnswer);
                  }
                }

                return (
                  <div
                    key={item.qid}
                    className={`p-3 rounded-lg border ${isCorrect ? "bg-green-50 border-green-200" : isBlank ? "bg-amber-50 border-amber-200" : "bg-red-50 border-red-200"
                      }`}>
                    <div className="flex items-start gap-2">
                      {isCorrect ? (
                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : isBlank ? (
                        <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                      )}

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-xs font-bold text-gray-900">Soru {item?.displayIndex}</span>
                          <span
                            className={`px-1.5 py-0.5 rounded-full text-[10px] font-medium ${isCorrect
                              ? "bg-green-100 text-green-800"
                              : isBlank
                                ? "bg-amber-100 text-amber-800"
                                : "bg-red-100 text-red-800"
                              }`}>
                            {isCorrect ? "Doğru" : isBlank ? "Boş" : "Yanlış"}
                          </span>
                        </div>

                        <div className="space-y-1">
                          <div className="flex items-start gap-2">
                            <span className="text-xs text-gray-600 font-medium min-w-[60px]">Cevabınız:</span>
                            <span
                              className={`text-xs font-medium ${isCorrect ? "text-green-700" : isBlank ? "text-amber-700" : "text-red-700"
                                }`}>
                              {isBlank ? "Boş" : studentAnswerStr}
                            </span>
                          </div>

                          {correctAnswerStr.trim() !== "" && (
                            <div className="flex items-start gap-2">
                              <span className="text-xs text-gray-600 font-medium min-w-[60px]">Doğru Cevap:</span>
                              <span className="text-xs font-medium text-green-700">{correctAnswerStr}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}